import type { IInput } from 'interfaces/IInput';
import { InputTaskCategory } from 'interfaces/IInputTask';
import { buildTaskMoaGroups } from 'pages/FarmsPage/Workspace/Tasks/taskCaption';
import { getInputYear } from 'utils/FarmUtils';

import { resolvePaddockId } from './computeLiveTotals';

/** How many earlier years of the same paddocks the Resistance section looks back over (OP-2563). */
export const RESISTANCE_HISTORY_YEARS = 3;

export interface ResistanceGroupRow {
  /** The MoA group as recorded on the product, e.g. "3" or "9B". */
  group: string;
  /** Planned applications using this group in the plans in scope. */
  current: number;
  /** Applications using this group on the same paddocks in the earlier years. */
  previous: number;
}

export interface ResistanceSummary {
  rows: ResistanceGroupRow[];
  /** The earlier years counted, oldest first, e.g. [2023, 2024, 2025]. Empty when the scope has no dated plan. */
  previousYears: number[];
  /** Earlier plans on the same paddocks with no task in the chosen source — not counted, so the user is told. */
  earlierPlansWithoutSource: number;
}

/**
 * Counts each MoA group once per task: a task is one application, so a
 * tank mix of two products in the same group counts once.
 */
const countGroups = (
  inputs: IInput[],
  category: InputTaskCategory,
  into: Map<string, number>
) => {
  for (const input of inputs) {
    for (const task of input.tasks) {
      if (task.category !== category) continue;
      for (const group of buildTaskMoaGroups(task)) {
        into.set(group, (into.get(group) ?? 0) + 1);
      }
    }
  }
};

/**
 * Live totals › Resistance (OP-2563): how often each mode of action group is
 * used in the plans in scope, and on the same paddocks in the
 * RESISTANCE_HISTORY_YEARS years before them.
 *
 * - Current: planned applications in `scopedInputs` (the tree selection and
 *   year filter, like the other Live totals sections).
 * - Earlier years: plans in `orgInputs` on the same paddocks, starting in the
 *   years before the latest year in scope, counting only `historySource`
 *   tasks (Actual or Plan — the user's choice, always named on screen).
 *   Earlier plans with no task in that source are counted separately so the
 *   panel can say they were left out.
 *
 * Rows are in group order (1, 2, …, 9A, 9B, …); groups only used earlier
 * still show, with 0 for the current plans.
 *
 * Pure function — no side effects, no hooks, no Redux.
 */
export const computeResistance = (
  scopedInputs: IInput[],
  orgInputs: IInput[],
  historySource: InputTaskCategory
): ResistanceSummary => {
  const current = new Map<string, number>();
  countGroups(scopedInputs, InputTaskCategory.Plan, current);

  const years = scopedInputs
    .map(getInputYear)
    .filter((year): year is number => year !== null);
  const previousYears =
    years.length > 0
      ? Array.from(
          { length: RESISTANCE_HISTORY_YEARS },
          (_, i) => Math.max(...years) - RESISTANCE_HISTORY_YEARS + i
        )
      : [];

  const paddockIds = new Set(
    scopedInputs
      .map(resolvePaddockId)
      .filter((id): id is string => id !== null)
  );
  const scopedIds = new Set(scopedInputs.map((input) => input.id));
  const earlierInputs = orgInputs.filter((input) => {
    if (scopedIds.has(input.id)) return false;
    const paddockId = resolvePaddockId(input);
    const year = getInputYear(input);
    return (
      paddockId !== null &&
      paddockIds.has(paddockId) &&
      year !== null &&
      previousYears.includes(year)
    );
  });
  const previous = new Map<string, number>();
  countGroups(earlierInputs, historySource, previous);
  const earlierPlansWithoutSource = earlierInputs.filter(
    (input) => !input.tasks.some((task) => task.category === historySource)
  ).length;

  const groups = Array.from(
    new Set(Array.from(current.keys()).concat(Array.from(previous.keys())))
  );
  groups.sort((a, b) =>
    a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' })
  );

  return {
    rows: groups.map((group) => ({
      group,
      current: current.get(group) ?? 0,
      previous: previous.get(group) ?? 0,
    })),
    previousYears,
    earlierPlansWithoutSource,
  };
};
